import { IProductOption } from "@faire/web-api/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { IProductsResponse } from "@faire/web-api/indigofair/data/IProductsResponse";
import { Column, Flex, HorizontalLine } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";

import {
  ISamplesBannerDetails,
  RequestSamplesCTA,
} from "@faire/retailer/components/Modals/SamplesRequestModal/RequestSamplesCTA";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useSettingEnablePdpAttributesEnrichment } from "@faire/retailer/settings/useSettingEnablePdpAttributesEnrichment";
import { ReportBrandIssuesButton } from "@faire/retailer/views/brand/ReportBrandIssues/ReportBrandIssuesButton";
import {
  ProductAttributeTagsMobile,
  ProductAttributeTagsTabletAndAbove,
} from "@faire/retailer/views/product/ProductDescriptionAccordions/ProductAttributeTags";
import { ProductDescriptionAccordion } from "@faire/retailer/views/product/ProductDescriptionAccordions/ProductDescriptionAccordion";
import { ProductDetailsAccordion } from "@faire/retailer/views/product/ProductDescriptionAccordions/ProductDetailsAccordion";
import { ProductEcoFriendlyInfoAccordion } from "@faire/retailer/views/product/ProductDescriptionAccordions/ProductEcoFriendlyInfoAccordion";

export const ProductDescriptionAccordions: React.FC<
  ProductDescriptionAccordions.IProps
> = ({
  className,
  selectedOption,
  productToken,
  productAttributeTags,
  brand,
  samplesBannerDetails,
  group,
}) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const pdpAttributesEnrichmentEnabled =
    useSettingEnablePdpAttributesEnrichment();

  return (
    <Wrapper className={className}>
      <StyledHorizontalLine />
      {productAttributeTags.length > 0 ? (
        <ResponsiveContentV2
          mobile={
            <ProductAttributeTagsMobile
              productAttributeTags={productAttributeTags}
            />
          }
          tabletAndAbove={
            <ProductAttributeTagsTabletAndAbove
              productAttributeTags={productAttributeTags}
            />
          }
        />
      ) : null}
      <Spacer
        height={{
          mobileAndAbove: "1/2x",
          tabletAndAbove: "3x",
        }}
      />
      {group?.map((section) => {
        const descriptionInformationSection = section.sections.find(
          (productInformation) => productInformation.type === "DESCRIPTION"
        );
        const detailsInformationSection = section.sections.find(
          (productInformation) => productInformation.type === "DETAILS"
        );
        const ecoFriendlyInfoSection = section.sections.find(
          (productInformation) => productInformation.type === "ECO_FRIENDLY"
        );
        return (
          <React.Fragment key={section.type}>
            <ProductDescriptionAccordion
              descriptionInformationSection={descriptionInformationSection}
            />
            {pdpAttributesEnrichmentEnabled ? null : (
              <ProductDetailsAccordion
                productOption={selectedOption}
                detailsInformationSection={detailsInformationSection}
                maxPreviewLines={section.max_preview_lines}
              />
            )}
            {ecoFriendlyInfoSection !== undefined ? (
              <ProductEcoFriendlyInfoAccordion
                ecoFriendlyInfoSection={ecoFriendlyInfoSection}
              />
            ) : null}
          </React.Fragment>
        );
      })}
      {isLoggedInRetailer ? (
        <ReportBrandWrapper>
          <ReportBrandIssuesButton
            brandToken={brand.token}
            productToken={productToken}
          />
        </ReportBrandWrapper>
      ) : null}
      {samplesBannerDetails !== undefined && brand.token !== undefined ? (
        <>
          <RequestSamplesCTA
            brandToken={brand.token}
            productToken={productToken}
            samplesBannerDetails={samplesBannerDetails}
          />
          <Spacer height="4x" />
        </>
      ) : null}
    </Wrapper>
  );
};

export namespace ProductDescriptionAccordions {
  export interface IProps {
    className?: string;
    selectedOption: IProductOption | undefined;
    productToken: string;
    productAttributeTags: IProductsResponse.IProductAttributeTag[];
    brand: IProductPageResponse.IPdpBrand;
    samplesBannerDetails: ISamplesBannerDetails | undefined;
    group:
      | IProductPageResponse.IPdpProduct.IDetails.IProductInformationSectionGroup[]
      | undefined;
  }
}

const ReportBrandWrapper = styled(Flex)`
  padding: ${getSpacing("3x", 0)};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("5x", 0)};
  }
`;

const StyledHorizontalLine = styled(HorizontalLine)`
  ${responsiveMediaQueries.tabletAndAbove} {
    display: none;
  }
`;

const Wrapper = styled(Column)`
  padding: ${getSpacing("2x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: 0;
  }
`;
